import React, { useState } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MailCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

export const CodeInput = ({ value, onChange, testid }) => (
  <InputOTP maxLength={6} value={value} onChange={onChange} data-testid={testid} autoFocus>
    <InputOTPGroup>
      {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={`slot-${i}`} index={i} className="w-11 h-12 text-lg mono" />)}
    </InputOTPGroup>
  </InputOTP>
);

const StepShell = ({ icon: Icon, title, text, children, onBack, testid }) => (
  <div className="w-full max-w-sm bg-white border border-slate-200 rounded-md p-8 animate-fade-up" data-testid={testid}>
    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
    <h2 className="text-2xl font-semibold tracking-tight mb-1">{title}</h2>
    <p className="text-sm text-slate-500 mb-6">{text}</p>
    {children}
    <button type="button" onClick={onBack} data-testid={`${testid}-back`} className="text-sm text-slate-500 hover:text-blue-700 underline underline-offset-4 mt-6 block mx-auto">
      Back to sign in
    </button>
  </div>
);

const useSubmit = (url, buildBody, onDone) => {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(url, buildBody());
      onDone(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return [busy, submit];
};

const resend = async (url, body, msg) => {
  try { await api.post(url, body); toast.success(msg); } catch (e) { toast.error(formatErr(e)); }
};

export const VerifyEmailStep = ({ email, onDone, onBack }) => {
  const [code, setCode] = useState("");
  const [busy, submit] = useSubmit("/auth/verify-email", () => ({ email, code }), onDone);
  return (
    <StepShell icon={MailCheck} title="Verify your email" testid="verify-email-step" onBack={onBack}
      text={<>We sent a 6-digit code to <b className="text-zinc-950">{email}</b>. Enter it to activate your account.</>}>
      <form onSubmit={submit} className="space-y-4">
        <CodeInput value={code} onChange={setCode} testid="verify-email-code" />
        <Button data-testid="verify-email-submit" type="submit" disabled={busy || code.length !== 6} className="w-full bg-blue-700 hover:bg-blue-800 text-white">
          {busy ? "Verifying…" : "Verify & continue"}
        </Button>
      </form>
      <button type="button" data-testid="verify-email-resend" onClick={() => resend("/auth/resend-verification", { email }, "A new code is on its way")}
        className="text-xs text-slate-600 hover:text-blue-700 underline underline-offset-4 mt-4">Didn't get it? Resend code</button>
    </StepShell>
  );
};

export const TwoFactorStep = ({ challenge, onDone, onBack }) => {
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [remember, setRemember] = useState(true);
  const isEmail = challenge.two_factor_method === "email";
  const [busy, submit] = useSubmit("/auth/2fa/verify",
    () => ({ challenge_token: challenge.challenge_token, code, remember_device: remember }), onDone);
  const text = useBackup ? "Enter one of the backup codes you saved when you turned on two-step sign-in."
    : isEmail ? <>We emailed a 6-digit sign-in code to <b className="text-zinc-950">{challenge.email}</b>.</>
    : "Open your authenticator app and enter the 6-digit code for KMS-Obligo.";
  return (
    <StepShell icon={ShieldCheck} title="Two-step sign-in" text={text} testid="two-factor-step" onBack={onBack}>
      <form onSubmit={submit} className="space-y-4">
        {useBackup
          ? <Input data-testid="two-factor-backup-input" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX" className="mono uppercase" />
          : <CodeInput value={code} onChange={setCode} testid="two-factor-code" />}
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <Checkbox data-testid="two-factor-remember" checked={remember} onCheckedChange={(v) => setRemember(v === true)} />
          Remember this device for 30 days
        </label>
        <Button data-testid="two-factor-submit" type="submit" disabled={busy || code.length < 6} className="w-full bg-blue-700 hover:bg-blue-800 text-white">
          {busy ? "Checking…" : "Verify & sign in"}
        </Button>
      </form>
      <div className="flex items-center justify-between mt-4 text-xs">
        <button type="button" data-testid="two-factor-toggle-backup" onClick={() => { setUseBackup(!useBackup); setCode(""); }}
          className="text-slate-600 hover:text-blue-700 underline underline-offset-4">{useBackup ? "Use 6-digit code" : "Use a backup code"}</button>
        {isEmail && !useBackup && (
          <button type="button" data-testid="two-factor-resend" onClick={() => resend("/auth/2fa/resend", { challenge_token: challenge.challenge_token }, "A new code is on its way")}
            className="text-slate-600 hover:text-blue-700 underline underline-offset-4">Resend code</button>
        )}
      </div>
    </StepShell>
  );
};

export const PendingAuthStep = ({ pending, onDone, onBack }) => (
  pending.verification_required
    ? <VerifyEmailStep email={pending.email} onDone={onDone} onBack={onBack} />
    : <TwoFactorStep challenge={pending} onDone={onDone} onBack={onBack} />
);
